import React, { useEffect, useRef, useState } from "react";

/**
 * AddressAutocomplete Component
 * 
 * Provides Google Places Autocomplete functionality for address input fields.
 * Automatically parses selected addresses into street address, zip code, and city.
 * 
 * Props:
 * - value: string - Current address value
 * - onChange: (address: string, zipCode: string, city: string) => void - Callback when address is selected
 * - onBlur: () => void - Callback when input loses focus
 * - className: string - CSS classes for the input
 * - required: boolean - Whether the field is required
 * - id: string - HTML id for the input
 * - name: string - HTML name for the input
 * 
 * Environment Variable Required:
 * - VITE_GOOGLE_PLACES_API_KEY - Google Places API key
 */
const AddressAutocomplete = ({
  value,
  onChange,
  onBlur,
  className = "w-full px-3 py-2 border rounded",
  required = false,
  id,
  name,
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(null);

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Load Google Places API script
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

    if (!apiKey) {
      setScriptError("Google Places API key is not configured");
      console.warn(
        "VITE_GOOGLE_PLACES_API_KEY environment variable is not set"
      );
      return;
    }

    // Check if script is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsScriptLoaded(true);
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    if (existingScript) {
      // Wait for script to load
      existingScript.addEventListener("load", () => {
        setIsScriptLoaded(true);
      });
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsScriptLoaded(true);
      }
      return;
    }

    // Create and load script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsScriptLoaded(true);
    };
    script.onerror = () => {
      setScriptError("Failed to load Google Places API");
      console.error("Failed to load Google Places API script");
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts (optional, usually not needed)
    };
  }, []);

  // Initialize autocomplete when script is loaded
  useEffect(() => {
    if (!isScriptLoaded || !inputRef.current || !window.google?.maps?.places) {
      return;
    }

    try {
      // Initialize autocomplete
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ["address"], // Restrict to addresses only
          componentRestrictions: {}, // Can be configured for specific countries if needed
          fields: ["address_components", "formatted_address"], // Request specific fields for better performance
        }
      );

      autocompleteRef.current = autocomplete;

      // Listen for place selection
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();

        if (!place) {
          return;
        }

        // Parse address components
        let streetNumber = "";
        let route = "";
        let postalCode = "";
        let city = "";

        if (place.address_components && place.address_components.length > 0) {
          place.address_components.forEach((component) => {
            const types = component.types;

            if (types.includes("street_number")) {
              streetNumber = component.long_name;
            } else if (types.includes("route")) {
              route = component.long_name;
            } else if (types.includes("postal_code")) {
              postalCode = component.long_name;
            } else if (types.includes("locality")) {
              city = component.long_name;
            } else if (types.includes("postal_town") && !city) {
              // Use postal_town if locality is not available
              city = component.long_name;
            }
          });
        }

        // Combine route and street number (Swedish format: street name first, then number)
        // If we don't have components, use the formatted address as fallback
        let address = [route, streetNumber].filter(Boolean).join(" ").trim();
        if (!address && place.formatted_address) {
          // Fallback to formatted address if parsing didn't work
          address = place.formatted_address;
        }

        // Call onChange callback with parsed values
        // Use empty strings if not found (so fields can be cleared if needed)
        if (onChangeRef.current) {
          onChangeRef.current(address || "", postalCode || "", city || "");
        }
      });
    } catch (error) {
      console.error("Error initializing Google Places Autocomplete:", error);
      setScriptError("Failed to initialize address autocomplete");
    }

    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(
          autocompleteRef.current
        );
        autocompleteRef.current = null;
      }
    };
  }, [isScriptLoaded]);

  // Handle manual input changes (when user types)
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    // When user types manually, only update address field
    // We pass undefined for zipCode and city to indicate they shouldn't be changed
    if (onChange) {
      onChange(newValue, undefined, undefined);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        value={value}
        onChange={handleInputChange}
        onBlur={onBlur}
        className={className}
        required={required}
        placeholder="Start typing an address..."
      />
      {scriptError && (
        <div className="text-yellow-600 text-xs mt-1">
          {scriptError} - Address autocomplete is disabled
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;

